import { ISiteWalkMedia } from '@api/models';
import { updatePolygonInSiteWalk } from '@api/services/sitewalk-gui.service';
import useDialog from '@hooks/use-dialog';
import { CLOSE_POLYGON_RADIUS } from 'modules/site-walk-gui/helper/constants';
import {
    distance,
    getNextZoneColor,
    levelsToPolygons,
    toNormalizedPoints,
} from 'modules/site-walk-gui/helper/floor-plan-gui.utils';
import { DragState, ISiteWalkMediaLevel, Point, Polygon, Tool } from 'modules/site-walk-gui/helper/types';
import { useCanvasSize } from 'modules/site-walk-gui/hooks/useCanvasSize';
import { useHitTesting } from 'modules/site-walk-gui/hooks/useFloorPlanImage';
import { useFloorPlanMedia } from 'modules/site-walk-gui/hooks/useFloorPlanMedia';
import useFloorPlanListData from 'modules/site-walk/hooks/useFloorPlanListData';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import FileUploadDrawer from '../FileUploadDrawer/FileUploadDrawer';
import { Sidebar } from '../Sidebar/Sidebar';
import { Toolbar } from '../Toolbar/Toolbar';

interface FloorPlanCustomizeGuiProps {
    floorPlansGui: ISiteWalkMedia;
    clientId: string;
    projectId: string;
    siteWalkMediaId: string;
    onSave?: (updatedFloorPlanGui: ISiteWalkMedia) => void;
}

const FloorPlanCustomizeGui = ({
    floorPlansGui,
    clientId,
    projectId,
    siteWalkMediaId,
    onSave,
}: FloorPlanCustomizeGuiProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const overlayRef = useRef<SVGSVGElement>(null);

    const media = useFloorPlanMedia(floorPlansGui.url, floorPlansGui.mediaType as 'IMAGE' | 'VIDEO');
    const canvasSize = useCanvasSize(containerRef, media);

    const [zoomEnabled, setZoomEnabled] = useState(false);
    const [scale, setScale] = useState(1);
    const [translate, setTranslate] = useState({ x: 0, y: 0 });
    const [isPanning, setIsPanning] = useState(false);
    const [isSpaceDown, setIsSpaceDown] = useState(false);
    const [panStart, setPanStart] = useState<Point | null>(null);
    const [panOrigin, setPanOrigin] = useState({ x: 0, y: 0 });

    const [isEditMode, setIsEditMode] = useState(false);
    const [activeTool, setActiveTool] = useState<Tool>('polygon');
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    const [polygons, setPolygons] = useState<Polygon[]>([]);
    const [selectedPolygonId, setSelectedPolygonId] = useState<string | null>(null);
    const [hoveredPolygonId, setHoveredPolygonId] = useState<string | null>(null);
    const [hoveredVertex, setHoveredVertex] = useState<{ polygonId: string; vertexIndex: number } | null>(null);
    const [inProgressPoints, setInProgressPoints] = useState<Point[]>([]);
    const [cursorPosition, setCursorPosition] = useState<Point | null>(null);
    const [dragState, setDragState] = useState<DragState | null>(null);
    const [openDeletePopover, setOpenDeletePopover] = useState(false);

    const { floorPlans, isLoading: floorPlanLoading } = useFloorPlanListData({ clientId, projectId });

    const uploadDrawerDialog = useDialog<string>();

    const { findVertexAt, findEdgeMidpointAt, findPolygonAt } = useHitTesting(polygons);

    const canSave = polygons.length > 0 && polygons.every((p) => p.floorPlan.trim() !== '');

    const videoRef = useRef<HTMLVideoElement | null>(null);
    const [mediaLoading, setMediaLoading] = useState(true);
    const [mediaLoadError, setMediaLoadError] = useState(false);

    useEffect(() => {
        if (!floorPlansGui.url) {
            setMediaLoading(false);
            return;
        }

        setMediaLoading(true);
        setMediaLoadError(false);
    }, [floorPlansGui.url, floorPlansGui.mediaType]);
    // useEffect(() => {
    //     if (!videoRef.current || floorPlansGui.mediaType !== 'VIDEO') return;

    //     if (isEditMode) {
    //         videoRef.current.pause();
    //     } else {
    //         videoRef.current.play().catch(() => {});
    //     }
    // }, [isEditMode, floorPlansGui.mediaType]);

    useEffect(() => {
        if (canvasSize.width === 0 || canvasSize.height === 0) return;
        setPolygons(levelsToPolygons(floorPlansGui.levels, canvasSize.width, canvasSize.height));
    }, [canvasSize.width, canvasSize.height, floorPlansGui.levels]);

    useEffect(() => {
        if (!selectedPolygonId) setOpenDeletePopover(false);
    }, [selectedPolygonId]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setInProgressPoints([]);
            if (e.code === 'Space') setIsSpaceDown(true);
            if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
                if (activeTool === 'polygon' && inProgressPoints.length > 0) {
                    e.preventDefault();
                    setInProgressPoints((prev) => prev.slice(0, -1));
                }
            }
        };

        const handleKeyUp = (e: KeyboardEvent) => {
            if (e.code === 'Space') {
                setIsSpaceDown(false);
                setIsPanning(false);
                setPanStart(null);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
        };
    }, [activeTool, inProgressPoints]);

    // const handleDeleteSelectedPolygon = () => {
    //     if (!selectedPolygonId) return;
    //     setPolygons((prev) => prev.filter((polygon) => polygon.id !== selectedPolygonId));
    //     setSelectedPolygonId(null);
    //     setOpenDeletePopover(false);
    // };

    // const zoomBy = (factor: number, centerX?: number, centerY?: number) => {
    //     if (!zoomEnabled) return;

    //     const container = containerRef.current;
    //     if (!container) return;

    //     const rect = container.getBoundingClientRect();

    //     // Default to container center if no point provided
    //     const cx = centerX ?? rect.width / 2;
    //     const cy = centerY ?? rect.height / 2;

    //     setScale((prevScale) => {
    //         const nextScale = Math.max(0.2, Math.min(6, prevScale * factor));

    //         // Adjust translate so the zoom focal point stays fixed
    //         setTranslate((prevTranslate) => ({
    //             x: cx - (cx - prevTranslate.x) * (nextScale / prevScale),
    //             y: cy - (cy - prevTranslate.y) * (nextScale / prevScale),
    //         }));

    //         return nextScale;
    //     });
    // };

    const resetZoom = () => {
        setScale(1);
        setTranslate({ x: 0, y: 0 });
    };
    // const onWheel = (e: React.WheelEvent) => {
    //     if (!zoomEnabled) return;
    //     e.preventDefault();

    //     const container = containerRef.current;
    //     if (!container) return;

    //     const rect = container.getBoundingClientRect();
    //     const cx = e.clientX - rect.left;
    //     const cy = e.clientY - rect.top;

    //     if (e.deltaY < 0) zoomBy(1.1, cx, cy);
    //     else zoomBy(0.9, cx, cy);
    // };

    const onWheel = (e: React.WheelEvent) => {
        if (!zoomEnabled) return;
        e.preventDefault();

        const container = containerRef.current;
        if (!container) return;

        const rect = container.getBoundingClientRect();
        const cx = e.clientX - rect.left;
        const cy = e.clientY - rect.top;

        const factor = e.deltaY < 0 ? 1.1 : 0.9;
        const nextScale = Math.max(0.2, Math.min(6, scale * factor));

        setScale(nextScale);
        setTranslate({
            x: cx - (cx - translate.x) * (nextScale / scale),
            y: cy - (cy - translate.y) * (nextScale / scale),
        });
    };

    const zoomBy = (factor: number) => {
        if (!zoomEnabled) return;

        const container = containerRef.current;
        if (!container) return;

        const rect = container.getBoundingClientRect();
        const cx = rect.width / 2;
        const cy = rect.height / 2;

        const nextScale = Math.max(0.2, Math.min(6, scale * factor));

        setScale(nextScale);
        setTranslate({
            x: cx - (cx - translate.x) * (nextScale / scale),
            y: cy - (cy - translate.y) * (nextScale / scale),
        });
    };
    const shouldStartPan = () => zoomEnabled && isSpaceDown;

    const screenToWorldPoint = (clientX: number, clientY: number): Point => {
        const container = containerRef.current!;
        const rect = container.getBoundingClientRect();

        const screenX = clientX - rect.left;
        const screenY = clientY - rect.top;

        return {
            x: (screenX - translate.x) / scale,
            y: (screenY - translate.y) / scale,
        };
    };

    const startVertexDrag = (polygonId: string, vertexIndex: number, startMouse: Point) => {
        const polygon = polygons.find((p) => p.id === polygonId)!;
        setDragState({
            type: 'vertex',
            polygonId,
            vertexIndex,
            startMouse,
            startPoints: polygon.points.map((pt) => ({ ...pt })),
        });
    };

    const startPolygonDrag = (polygon: Polygon, startMouse: Point) => {
        setDragState({
            type: 'polygon',
            polygonId: polygon.id,
            startMouse,
            startPoints: polygon.points.map((pt) => ({ ...pt })),
        });
    };

    const applyDragDelta = (dx: number, dy: number) => {
        setPolygons((prev) =>
            prev.map((polygon) => {
                if (polygon.id !== dragState!.polygonId) return polygon;

                if (dragState!.type === 'vertex' && dragState!.vertexIndex !== undefined) {
                    return {
                        ...polygon,
                        points: polygon.points.map((pt, i) =>
                            i === dragState!.vertexIndex
                                ? { x: dragState!.startPoints[i].x + dx, y: dragState!.startPoints[i].y + dy }
                                : pt
                        ),
                    };
                }

                return {
                    ...polygon,
                    points: dragState!.startPoints.map((pt) => ({ x: pt.x + dx, y: pt.y + dy })),
                };
            })
        );
    };

    const closeInProgressPolygon = () => {
        setPolygons((prev) => [
            ...prev,
            {
                id: crypto.randomUUID(),
                floorPlan: '',
                points: inProgressPoints,
                label: 'Floor Plan',
                color: getNextZoneColor(prev),
            },
        ]);
        setInProgressPoints([]);
    };

    const insertVertexAtEdge = (edgeIndex: number, point: Point) => {
        setPolygons((prev) =>
            prev.map((polygon) => {
                if (polygon.id !== selectedPolygonId) return polygon;
                const newPoints = [...polygon.points];
                newPoints.splice(edgeIndex + 1, 0, point);
                return { ...polygon, points: newPoints };
            })
        );
    };

    const removeVertex = (polygonId: string, vertexIndex: number) => {
        setPolygons((prev) =>
            prev.map((polygon) => {
                if (polygon.id !== polygonId || polygon.points.length <= 3) return polygon;
                return { ...polygon, points: polygon.points.filter((_, i) => i !== vertexIndex) };
            })
        );
    };

    const handlePointerDown = (e: React.PointerEvent<SVGSVGElement>) => {
        const point = screenToWorldPoint(e.clientX, e.clientY);

        if (shouldStartPan()) {
            setIsPanning(true);
            setPanStart({ x: e.clientX, y: e.clientY });
            setPanOrigin({ ...translate });
            return;
        }

        if (!isEditMode) {
            const polygon = findPolygonAt(point);
            setSelectedPolygonId(polygon?.id ?? null);
            return;
        }

        if (activeTool === 'select') {
            const vertexHit = findVertexAt(point, selectedPolygonId ?? undefined);
            if (vertexHit) {
                setSelectedPolygonId(vertexHit.polygonId);
                startVertexDrag(vertexHit.polygonId, vertexHit.vertexIndex, point);
                return;
            }

            if (selectedPolygonId) {
                const edgeIndex = findEdgeMidpointAt(point, selectedPolygonId);
                if (edgeIndex !== null) {
                    insertVertexAtEdge(edgeIndex, point);
                    return;
                }
            }

            const polygon = findPolygonAt(point);
            if (polygon) {
                setSelectedPolygonId(polygon.id);
                startPolygonDrag(polygon, point);
                return;
            }

            setSelectedPolygonId(null);
            return;
        }

        if (activeTool === 'polygon') {
            if (inProgressPoints.length >= 3 && distance(point, inProgressPoints[0]) <= CLOSE_POLYGON_RADIUS) {
                closeInProgressPolygon();
                return;
            }

            setInProgressPoints((prev) => [...prev, point]);
            return;
        }
    };

    const handlePointerMove = (e: React.PointerEvent<SVGSVGElement>) => {
        const point = screenToWorldPoint(e.clientX, e.clientY);
        setCursorPosition(point);

        if (isPanning && panStart) {
            const dx = e.clientX - panStart.x;
            const dy = e.clientY - panStart.y;

            setTranslate({
                x: panOrigin.x + dx,
                y: panOrigin.y + dy,
            });
            return;
        }

        if (dragState) {
            const dx = point.x - dragState.startMouse.x;
            const dy = point.y - dragState.startMouse.y;
            applyDragDelta(dx, dy);
            return;
        }

        const vertexHit = findVertexAt(point, selectedPolygonId ?? undefined);
        setHoveredVertex(vertexHit);

        const polygon = findPolygonAt(point);
        setHoveredPolygonId(polygon?.id ?? null);
    };

    const handlePointerUp = () => {
        setDragState(null);
        setIsPanning(false);
        setPanStart(null);
    };

    const handleDoubleClick = () => {
        if (activeTool === 'polygon' && inProgressPoints.length >= 3) {
            closeInProgressPolygon();
        }
    };

    const handleDeleteSelectedPolygon = async () => {
        if (!selectedPolygonId) return;

        try {
            setIsSaving(true);
            setSaveError(null);

            const updatedPolygons = polygons.filter((polygon) => polygon.id !== selectedPolygonId);

            const levels: ISiteWalkMediaLevel[] = updatedPolygons.map((polygon) => ({
                floorPlan: polygon.floorPlan,
                color: polygon.color,
                points: toNormalizedPoints(polygon.points, canvasSize.width, canvasSize.height),
            }));

            await updatePolygonInSiteWalk(clientId, projectId, siteWalkMediaId, levels);

            setPolygons(updatedPolygons);
            setSelectedPolygonId(null);
            setOpenDeletePopover(false);

            toast.success('Polygon deleted successfully');
        } catch (error: any) {
            setSaveError(error?.message || 'Failed to delete polygon');
            toast.error(error?.message || 'Failed to delete polygon');
        } finally {
            setIsSaving(false);
        }
    };

    const updateSelectedPolygon = (updates: Partial<Polygon>) => {
        if (!selectedPolygonId) return;
        setPolygons((prev) =>
            prev.map((polygon) => (polygon.id === selectedPolygonId ? { ...polygon, ...updates } : polygon))
        );
    };

    const selectedPolygon = useMemo(
        () => polygons.find((polygon) => polygon.id === selectedPolygonId) ?? null,
        [polygons, selectedPolygonId]
    );

    const handleSave = async () => {
        try {
            setIsSaving(true);
            setSaveError(null);

            const levels: ISiteWalkMediaLevel[] = polygons.map((polygon) => ({
                floorPlan: polygon.floorPlan,
                color: polygon.color,
                points: toNormalizedPoints(polygon.points, canvasSize.width, canvasSize.height),
            }));

            onSave?.({
                ...floorPlansGui,
                levels,
            });

            await updatePolygonInSiteWalk(clientId, projectId, siteWalkMediaId, levels);
            toast.success('Floor Plan Updated');
        } catch (error: any) {
            setSaveError(error?.message || 'Failed to save polygons');
            toast.error(error?.message || 'Failed to save polygons');
        } finally {
            setIsSaving(false);
        }
    };

    const zoomPercent = Math.round(scale * 100);

    const renderPolygonPoints = (points: Point[]) => points.map((p) => `${p.x},${p.y}`).join(' ');
    const handleSelectTool = (tool: Tool) => {
        setActiveTool(tool);
        if (tool !== 'select') setSelectedPolygonId(null);
        if (tool !== 'polygon') setInProgressPoints([]);
    };

    const handleSelectPolygon = (id: string) => {
        setSelectedPolygonId(id);
        setActiveTool('select');
    };
    const handleEnterEdit = () => {
        setIsEditMode(true);
        setActiveTool('polygon');
        setSaveError(null);
    };

    const handleCancelEdit = () => {
        setPolygons(levelsToPolygons(floorPlansGui.levels, canvasSize.width, canvasSize.height));
        setIsEditMode(false);
        setSelectedPolygonId(null);
        setInProgressPoints([]);
        setSaveError(null);
    };

    const handleUpload = () => {
        uploadDrawerDialog.show(siteWalkMediaId);
    };

    const handleClearAll = () => {
        setPolygons([]);
        setSelectedPolygonId(null);
        setInProgressPoints([]);
        setDragState(null);
        setHoveredPolygonId(null);
        setHoveredVertex(null);
    };

    {
        mediaLoading && (
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-slate-950/60">
                <div className="flex flex-col items-center gap-3">
                    <div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-600 border-t-white" />
                    <p className="text-sm text-white">Loading {floorPlansGui.mediaType.toLowerCase()}...</p>
                </div>
            </div>
        );
    }

    {
        mediaLoadError && !mediaLoading && (
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-slate-950/70">
                <p className="text-sm text-red-300">Failed to load {floorPlansGui.mediaType.toLowerCase()}.</p>
            </div>
        );
    }

    return (
        <div className="w-full h-full flex bg-slate-950 text-white overflow-hidden">
            <div className="shrink-0 border-r border-white/10 bg-[#1e293b]">
                <Sidebar
                    isEditMode={isEditMode}
                    activeTool={activeTool}
                    polygons={polygons}
                    selectedPolygonId={selectedPolygonId}
                    selectedPolygon={selectedPolygon}
                    onSelectTool={handleSelectTool}
                    onSelectPolygon={handleSelectPolygon}
                    onChangePolygonColor={(color) => updateSelectedPolygon({ color })}
                    onChangePolygonFloorPlan={(floorPlan) =>
                        updateSelectedPolygon({ floorPlan, label: floorPlan || 'Floor Plan' })
                    }
                    floorPlans={floorPlans}
                    floorPlanLoading={floorPlanLoading}
                />
            </div>

            <div className="flex-1 min-w-0 min-h-0 flex flex-col">
                <Toolbar
                    isEditMode={isEditMode}
                    activeTool={activeTool}
                    inProgressPoints={inProgressPoints}
                    selectedPolygon={selectedPolygon}
                    polygonCount={polygons.length}
                    isSaving={isSaving}
                    canSave={canSave}
                    saveError={saveError}
                    openDeletePopover={openDeletePopover}
                    setOpenDeletePopover={setOpenDeletePopover}
                    onEnterEdit={handleEnterEdit}
                    onSave={handleSave}
                    onUpload={handleUpload}
                    onCancelEdit={handleCancelEdit}
                    onDeleteSelected={handleDeleteSelectedPolygon}
                    onClearAll={handleClearAll}
                    onChangePolygonColor={(color) => updateSelectedPolygon({ color })}
                    onChangePolygonFloorPlan={(floorPlan) =>
                        updateSelectedPolygon({ floorPlan, label: floorPlan || 'Floor Plan' })
                    }
                />

                <div className="flex-1 min-h-0 flex">
                    <div className="flex-1 min-w-0 min-h-0 p-4">
                        <div className="h-full rounded-xl border border-slate-800 bg-slate-900 overflow-hidden">
                            <div
                                ref={containerRef}
                                className="relative mx-auto select-none w-full h-full"
                                style={{
                                    width: canvasSize.width || '100%',
                                    height: canvasSize.height || 500,
                                }}
                                onWheel={onWheel}
                            >
                                {/* media + svg */}
                                <div
                                    style={{
                                        transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
                                        transformOrigin: 'top left',
                                        width: canvasSize.width,
                                        height: canvasSize.height,
                                        position: 'absolute',
                                        left: 0,
                                        top: 0,
                                    }}
                                >
                                    {floorPlansGui.mediaType === 'IMAGE' && (
                                        <img
                                            src={floorPlansGui.url}
                                            alt="Floor plan"
                                            className="block w-full h-full object-contain pointer-events-none"
                                            draggable={false}
                                            onLoad={() => {
                                                setMediaLoading(false);
                                                setMediaLoadError(false);
                                            }}
                                            onError={() => {
                                                setMediaLoading(false);
                                                setMediaLoadError(true);
                                            }}
                                        />
                                    )}

                                    {floorPlansGui.mediaType === 'VIDEO' && (
                                        <video
                                            ref={videoRef}
                                            src={floorPlansGui.url}
                                            className="block w-full h-full object-contain bg-black pointer-events-none"
                                            controls={false}
                                            autoPlay={!isEditMode}
                                            muted
                                            loop={!isEditMode}
                                            playsInline
                                            onLoadedData={() => {
                                                setMediaLoading(false);
                                                setMediaLoadError(false);
                                            }}
                                            onError={() => {
                                                setMediaLoading(false);
                                                setMediaLoadError(true);
                                            }}
                                        />
                                    )}

                                    <svg
                                        ref={overlayRef}
                                        width={canvasSize.width}
                                        height={canvasSize.height}
                                        viewBox={`0 0 ${canvasSize.width} ${canvasSize.height}`}
                                        className="absolute inset-0"
                                        onPointerDown={handlePointerDown}
                                        onPointerMove={handlePointerMove}
                                        onPointerUp={handlePointerUp}
                                        onPointerLeave={handlePointerUp}
                                        onDoubleClick={handleDoubleClick}
                                        onClick={(e) => {
                                            if (!isEditMode && e.target === e.currentTarget) {
                                                setSelectedPolygonId(null);
                                                setHoveredPolygonId(null);
                                            }
                                        }}
                                        style={{
                                            cursor: isPanning
                                                ? 'grabbing'
                                                : shouldStartPan()
                                                ? 'grab'
                                                : !isEditMode
                                                ? 'pointer'
                                                : activeTool === 'polygon'
                                                ? 'crosshair'
                                                : 'default',
                                        }}
                                    >
                                        {polygons.map((polygon) => {
                                            const isSelected = polygon.id === selectedPolygonId;
                                            const isHovered = polygon.id === hoveredPolygonId;

                                            return (
                                                <g key={polygon.id}>
                                                    <polygon
                                                        points={renderPolygonPoints(polygon.points)}
                                                        fill={
                                                            isSelected
                                                                ? `${polygon.color}33`
                                                                : isHovered
                                                                ? `${polygon.color}5E`
                                                                : `${polygon.color}20`
                                                        }
                                                        stroke={polygon.color}
                                                        strokeWidth={isSelected ? 3 : isHovered ? 2.5 : 2}
                                                        style={{
                                                            cursor: !isEditMode ? 'pointer' : undefined,
                                                            pointerEvents: 'auto',
                                                        }}
                                                        onMouseEnter={() => {
                                                            if (!isEditMode) setHoveredPolygonId(polygon.id);
                                                        }}
                                                        onMouseLeave={() => {
                                                            if (!isEditMode)
                                                                setHoveredPolygonId((prev) =>
                                                                    prev === polygon.id ? null : prev
                                                                );
                                                        }}
                                                        onClick={(e) => {
                                                            if (!isEditMode) {
                                                                e.stopPropagation();
                                                                console.log('clicked');
                                                                // setSelectedPolygonId(polygon.id);
                                                            }
                                                        }}
                                                    />

                                                    {polygon.points.map((point, index) => {
                                                        const isVertexHovered =
                                                            hoveredVertex?.polygonId === polygon.id &&
                                                            hoveredVertex?.vertexIndex === index;

                                                        if (!isEditMode && !isSelected) return null;
                                                        if (!isSelected && !isVertexHovered) return null;

                                                        return (
                                                            <circle
                                                                key={`${polygon.id}-${index}`}
                                                                cx={point.x}
                                                                cy={point.y}
                                                                r={isVertexHovered ? 7 : 5}
                                                                fill={polygon.color}
                                                                stroke="#fff"
                                                                strokeWidth={2}
                                                                onDoubleClick={(e) => {
                                                                    e.stopPropagation();
                                                                    removeVertex(polygon.id, index);
                                                                }}
                                                            />
                                                        );
                                                    })}

                                                    {isSelected &&
                                                        polygon.points.map((point, index) => {
                                                            const next =
                                                                polygon.points[(index + 1) % polygon.points.length];
                                                            const midpoint = {
                                                                x: (point.x + next.x) / 2,
                                                                y: (point.y + next.y) / 2,
                                                            };

                                                            return (
                                                                <circle
                                                                    key={`${polygon.id}-mid-${index}`}
                                                                    cx={midpoint.x}
                                                                    cy={midpoint.y}
                                                                    r={4}
                                                                    fill="#fff"
                                                                    opacity={0.7}
                                                                />
                                                            );
                                                        })}

                                                    {polygon.label && polygon.points[0] && (
                                                        <text
                                                            x={polygon.points[0].x + 8}
                                                            y={polygon.points[0].y - 8}
                                                            fill="#fff"
                                                            fontSize="12"
                                                            fontWeight="600"
                                                        >
                                                            {polygon.label}
                                                        </text>
                                                    )}
                                                </g>
                                            );
                                        })}

                                        {inProgressPoints.length > 0 && (
                                            <>
                                                <polyline
                                                    points={renderPolygonPoints(
                                                        cursorPosition
                                                            ? [...inProgressPoints, cursorPosition]
                                                            : inProgressPoints
                                                    )}
                                                    fill="none"
                                                    stroke="#22c55e"
                                                    strokeWidth={2}
                                                    strokeDasharray="6 4"
                                                />

                                                {inProgressPoints.map((point, index) => (
                                                    <circle
                                                        key={`in-progress-${index}`}
                                                        cx={point.x}
                                                        cy={point.y}
                                                        r={index === 0 ? CLOSE_POLYGON_RADIUS : 4}
                                                        fill={index === 0 ? '#22c55e33' : '#22c55e'}
                                                        stroke="#22c55e"
                                                        strokeWidth={2}
                                                    />
                                                ))}
                                            </>
                                        )}
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="px-4 pb-4">
                    <div className="h-12 rounded-xl border border-slate-800 bg-slate-900 px-4 flex items-center justify-between">
                        <div className="flex items-center gap-3 text-sm text-slate-400">
                            <span>Media: {floorPlansGui.mediaType}</span>
                            <span>Zones: {polygons.length}</span>
                            {selectedPolygon && <span>Selected: {selectedPolygon.label || 'Floor Plan'}</span>}
                        </div>

                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setZoomEnabled((prev) => !prev)}
                                className="px-3 h-8 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm"
                            >
                                Zoom {zoomEnabled ? 'ON' : 'OFF'}
                            </button>

                            <div
                                className={`flex items-center gap-1 transition-opacity ${
                                    zoomEnabled ? 'opacity-100' : 'opacity-30 pointer-events-none'
                                }`}
                            >
                                {/* <button
                                    onClick={() => zoomBy(0.8)}
                                    className="w-8 h-8 rounded-lg bg-slate-800 hover:bg-slate-700"
                                    title="Zoom out"
                                >
                                    -
                                </button> */}

                                <button
                                    onClick={resetZoom}
                                    className="w-14 h-8 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm"
                                    title="Reset zoom"
                                >
                                    {zoomPercent}%
                                </button>

                                {/* <button
                                    onClick={() => zoomBy(0.5)}
                                    className="w-8 h-8 rounded-lg bg-slate-800 hover:bg-slate-700"
                                    title="Zoom in"
                                >
                                    +
                                </button> */}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <FileUploadDrawer
                show={uploadDrawerDialog.isOpen}
                onClose={uploadDrawerDialog.hide}
                siteWalkMediaId={uploadDrawerDialog.data}
                clientId={clientId}
                projectId={projectId}
                onSave={onSave}
            />
        </div>
    );
};

export default FloorPlanCustomizeGui;
